import { DateBuilder } from '~/builders/project-dates/DateBuilder';
import { DateCalculationEngine } from '~/engine/processing/shared/date-calculations';
import type { BondSeries } from '~/types';
import type { DateRecordsMapByPaymentDate } from '~/types/project';

/**
 * Utility functions for calculating project timeline from bond series and date records
 */

/**
 * Finds the project timeline (start and end years) from bond series and date records
 * @param startingTaxYear The starting tax year from date inputs
 * @param bondSeries Array of bond series to analyze
 * @param dateRecordsMapByPaymentDate Map of payment dates to date records
 * @returns Object with startYear and endYear
 */
export function calculateProjectTimeline(
  startingTaxYear: number,
  bondSeries: BondSeries[],
  dateRecordsMapByPaymentDate: DateRecordsMapByPaymentDate
): { startYear: number; endYear: number } {
  const startYear = startingTaxYear;

  // Find the latest tax year from all bond series final payment dates
  const endYear = bondSeries.reduce((latest: number, bond: BondSeries) => {
    const finalDateKey = DateBuilder.constructPaymentDateKey(bond.finalPrincipalPaymentDate);
    const finalDateKeyStr = finalDateKey.toString();
    const dateRecord = dateRecordsMapByPaymentDate[finalDateKeyStr];

    if (!dateRecord) {
      console.warn(`No date record found for payment date key: ${finalDateKeyStr}`);
      return latest;
    }

    const taxYear = dateRecord.taxYear;
    return taxYear > latest ? taxYear : latest;
  }, startYear);

  return { startYear, endYear };
}

/**
 * Finds the latest payment date from all bond series
 * @param bondSeries Array of bond series to analyze
 * @returns The latest final principal payment date
 */
export function findLatestPaymentDate(bondSeries: BondSeries[]): string {
  return bondSeries.reduce((latest: string, bond: BondSeries) => {
    return DateCalculationEngine.isDateAfter(bond.finalPrincipalPaymentDate, latest)
      ? bond.finalPrincipalPaymentDate
      : latest;
  }, bondSeries[0]?.finalPrincipalPaymentDate || '');
}

/**
 * Finds the latest tax year from all bond series
 * @param bondSeries Array of bond series to analyze
 * @param dateRecordsMapByPaymentDate Map of payment dates to date records
 * @returns The latest tax year
 */
export function findLatestTaxYear(
  bondSeries: BondSeries[],
  dateRecordsMapByPaymentDate: DateRecordsMapByPaymentDate
): number {
  return bondSeries.reduce((latest: number, bond: BondSeries) => {
    const finalDateKey = DateBuilder.constructPaymentDateKey(bond.finalPrincipalPaymentDate);
    const finalDateKeyStr = finalDateKey.toString();
    const dateRecord = dateRecordsMapByPaymentDate[finalDateKeyStr];

    if (!dateRecord) {
      console.warn(`No date record found for payment date key: ${finalDateKeyStr}`);
      return latest;
    }

    const taxYear = dateRecord.taxYear;
    return taxYear > latest ? taxYear : latest;
  }, 0);
}
